import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { Chart as ChartCanvas } from "react-chartjs-2";

import { formatCents } from "../../../utils/directorFormat";
import AmpliObrasLink from "./AmpliObrasLink";
import { addMonths, money, monthLabel } from "./format";

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Tooltip, Legend);

const HORIZON = 12;
const reais = (cents) => cents / 100;

// Agrega as séries já calculadas pelo BE da seleção (uma obra ou o escopo inteiro) nos 12 meses
// a partir da data-base: entrada e saída do mês, saldo do mês e acumulado desde hoje.
const monthsFor = (rows, asOf) => {
  const byMonth = rows.map((row) => new Map((row.series || []).map((point) => [point.month, point])));
  return Array.from({ length: HORIZON }, (_, index) => {
    const month = addMonths(asOf, index);
    let inCents = 0;
    let outCents = 0;
    let netCents = 0;
    let cumCents = 0;
    rows.forEach((row, rowIndex) => {
      const point = byMonth[rowIndex].get(month);
      inCents += point?.inCents || 0;
      outCents += point?.outCents || 0;
      const next = (row.metrics?.gap?.next || []).find((item) => item.month === month);
      netCents += next?.netCents || 0;
      cumCents += next?.cumCents || 0;
    });
    return { month, inCents, outCents, netCents, cumCents };
  });
};

const CashChart = ({ data, rows, focus, onFocus }) => {
  const selected = focus ? rows.filter((row) => row.key === focus) : rows;
  const focusedRow = focus ? selected[0] : null;
  const months = monthsFor(selected.length ? selected : rows, data.asOf);
  const chartData = {
    labels: months.map((item) => monthLabel(item.month)),
    datasets: [
      {
        type: "line",
        label: "Acumulado",
        data: months.map((item) => reais(item.cumCents)),
        borderColor: "#245AE4",
        backgroundColor: "#245AE4",
        borderWidth: 2.5,
        pointRadius: 3,
        pointHoverRadius: 5,
        tension: 0.2,
        order: 0,
      },
      {
        type: "bar",
        label: "Entradas",
        data: months.map((item) => reais(item.inCents)),
        backgroundColor: "#2a9d70",
        borderRadius: 4,
        order: 1,
      },
      {
        type: "bar",
        label: "Saídas",
        data: months.map((item) => reais(item.outCents)),
        backgroundColor: "#FD3400",
        borderRadius: 4,
        order: 1,
      },
    ],
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { intersect: false, mode: "index" },
    plugins: {
      legend: { position: "bottom", labels: { usePointStyle: true, boxWidth: 8 } },
      datalabels: { display: false },
      tooltip: { callbacks: { label: (context) => `${context.dataset.label}: ${formatCents(Math.round(context.parsed.y * 100))}` } },
    },
    scales: {
      x: { grid: { display: false } },
      y: { ticks: { callback: (value) => money(Math.round(Number(value) * 100)) } },
    },
  };

  return <section className="director-runway director-dashboard-cash" aria-labelledby="director-dashboard-cash-title">
    <header>
      <div>
        <span className="director-eyebrow">Caixa · próximos 12 meses</span>
        <h2 id="director-dashboard-cash-title">Vai faltar dinheiro?</h2>
      </div>
      <div className="director-filter-field director-dashboard-cash__focus">
        <label htmlFor="director-dashboard-cash-focus">Obra</label>
        <select id="director-dashboard-cash-focus" value={focus || ""} onChange={(event) => onFocus(event.target.value || null)}>
          <option value="">{data.scopeMode === "all" ? "Tudo" : "Todas as obras"}</option>
          {rows.map((row) => <option key={row.key} value={row.key}>{row.name}</option>)}
        </select>
        <AmpliObrasLink className="director-dashboard-cash__open" wbsBuildingId={focusedRow && !focusedRow.overhead ? focusedRow.wbsBuildingId : null}>
          Abrir no Ampli Obras ↗
        </AmpliObrasLink>
      </div>
    </header>
    <p className="director-dashboard-lede">
      Barras: o que entra e o que sai no mês. Linha: o acumulado desde hoje. Abaixo de zero é dinheiro que a empresa precisa colocar.
    </p>
    <div className="director-runway__canvas" aria-hidden="true">
      <ChartCanvas type="bar" data={chartData} options={options} />
    </div>
    <div className="director-table-scroll">
      <table className="director-table director-dashboard-table" aria-label="Caixa mês a mês">
        <thead>
          <tr>
            <th scope="col">Mês</th>
            <th scope="col">Entra</th>
            <th scope="col">Sai</th>
            <th scope="col">Saldo do mês</th>
            <th scope="col">Acumulado</th>
          </tr>
        </thead>
        <tbody>
          {months.map((item) => <tr key={item.month}>
            <th scope="row">{monthLabel(item.month)}</th>
            <td>{money(item.inCents)}</td>
            <td>{money(item.outCents)}</td>
            <td data-tone={item.netCents < 0 ? "bad" : "good"}>
              {`${item.netCents < 0 ? "Falta" : "Sobra"} ${money(Math.abs(item.netCents))}`}
            </td>
            <td data-tone={item.cumCents < 0 ? "bad" : undefined}>{money(item.cumCents)}</td>
          </tr>)}
        </tbody>
      </table>
    </div>
  </section>;
};

export default CashChart;
